import { QuizQuestion } from '../quizEngine';

// Canton & DAML: 100 questions (30 easy, 40 average, 30 very hard).
export const cantonDamlQuestions: QuizQuestion[] = [
    // --- EASY (30) ---
    {
        id: 1,
        difficulty: 'easy',
        question: 'DAML is a language for writing applications that run on which platform?',
        choices: { a: 'The Canton network / ledger', b: 'The Ethereum Virtual Machine', c: 'The Solana runtime', d: 'The Bitcoin Script interpreter' },
        answer: 'a',
        explanation: 'DAML smart contracts run on Canton, a privacy-focused distributed ledger network.',
    },
    {
        id: 2,
        difficulty: 'easy',
        question: "Which language most influenced DAML's syntax and semantics?",
        choices: { a: 'C++', b: 'Haskell', c: 'Python', d: 'Scala' },
        answer: 'b',
        explanation: 'DAML is a pure functional language with strong static typing, closely modelled on Haskell.',
    },
    {
        id: 3,
        difficulty: 'easy',
        question: 'In DAML, the on-ledger unit of data is called a:',
        choices: { a: 'block', b: 'contract', c: 'row', d: 'account' },
        answer: 'b',
        explanation: 'A template is the blueprint; the ledger holds contracts, which are instances of templates.',
    },
    {
        id: 4,
        difficulty: 'easy',
        question: 'A DAML `template` is most analogous to:',
        choices: {
            a: 'a class / blueprint for creating contracts',
            b: 'a database migration that creates tables',
            c: 'a function that runs on every transaction',
            d: "a key pair that signs on a party's behalf",
        },
        answer: 'a',
        explanation: 'A template declares the fields, parties and choices that its contract instances will have.',
    },
    {
        id: 5,
        difficulty: 'easy',
        question: 'The parties that must authorize a contract and are bound by it are its:',
        choices: { a: 'observers', b: 'maintainers', c: 'signatories', d: 'controllers' },
        answer: 'c',
        explanation: 'Signatories authorize creation and archival and are the parties legally/logically bound by the contract.',
    },
    {
        id: 6,
        difficulty: 'easy',
        question: 'Parties who are allowed to see a contract but are not bound by it are:',
        choices: { a: 'signatories', b: 'observers', c: 'validators', d: 'sequencers' },
        answer: 'b',
        explanation: 'Observers get visibility of the contract without authorization power over it.',
    },
    {
        id: 7,
        difficulty: 'easy',
        question: 'Which keyword defines an action a party can take on a contract?',
        choices: { a: 'choice', b: 'method', c: 'trigger', d: 'event' },
        answer: 'a',
        explanation: 'A `choice` is an exercisable action; its body is an `Update` describing the ledger changes.',
    },
    {
        id: 8,
        difficulty: 'easy',
        question: 'By default, exercising a choice (not marked `nonconsuming`) does what to the contract?',
        choices: { a: 'Duplicates it', b: 'Archives (consumes) it', c: 'Leaves it unchanged', d: 'Transfers it to the controller' },
        answer: 'b',
        explanation: 'A plain choice is consuming: the contract is archived as part of exercising it.',
    },
    {
        id: 9,
        difficulty: 'easy',
        question: 'To make a choice that does NOT archive the contract, you mark it:',
        choices: { a: 'preconsuming', b: 'nonconsuming', c: 'readonly', d: 'static' },
        answer: 'b',
        explanation: '`nonconsuming` choices leave the contract active, useful for repeated actions or queries.',
    },
    {
        id: 10,
        difficulty: 'easy',
        question: 'The party allowed to exercise a given choice is the:',
        choices: { a: 'signatory', b: 'observer', c: 'controller', d: 'maintainer' },
        answer: 'c',
        explanation: 'Each choice names one or more controllers who are authorized to exercise it.',
    },
    {
        id: 11,
        difficulty: 'easy',
        question: 'Which DAML type represents a ledger participant identity?',
        choices: { a: 'Address', b: 'Party', c: 'Account', d: 'Principal' },
        answer: 'b',
        explanation: '`Party` is an opaque identifier for an actor on the ledger.',
    },
    {
        id: 12,
        difficulty: 'easy',
        question: 'A reference to a specific active contract on the ledger has type:',
        choices: { a: 'ContractId', b: 'Hash', c: 'Ref', d: 'Pointer' },
        answer: 'a',
        explanation: '`ContractId t` identifies a particular active contract of template `t`.',
    },
    {
        id: 13,
        difficulty: 'easy',
        question: 'Which built-in adds a new contract to the ledger inside a transaction?',
        choices: { a: 'insert', b: 'create', c: 'mint', d: 'commit' },
        answer: 'b',
        explanation: '`create` produces an `Update (ContractId t)` for the new contract.',
    },
    {
        id: 14,
        difficulty: 'easy',
        question: 'Which built-in runs a choice on an existing contract?',
        choices: { a: 'call', b: 'invoke', c: 'exercise', d: 'apply' },
        answer: 'c',
        explanation: '`exercise cid ChoiceName with ...` performs the choice.',
    },
    {
        id: 15,
        difficulty: 'easy',
        question: 'DAML ledger history is:',
        choices: {
            a: 'mutable: contracts are edited in place',
            b: 'append-only: contracts are created and archived',
            c: 'stored off-ledger, with only hashes on-chain',
            d: 'rewritten by the sequencer on every block',
        },
        answer: 'b',
        explanation: 'You never mutate a contract; you archive it and create a new one to represent a change.',
    },
    {
        id: 16,
        difficulty: 'easy',
        question: 'The `ensure` clause in a template specifies:',
        choices: {
            a: 'a precondition that must hold for the contract to be created',
            b: 'the maximum amount of work a choice may perform before failing',
            c: 'an invariant rechecked each time a choice is exercised',
            d: 'an event the ledger emits when the contract is created',
        },
        answer: 'a',
        explanation: 'If `ensure` evaluates to `False`, the `create` fails and the transaction aborts. It is checked at creation only.',
    },
    {
        id: 17,
        difficulty: 'easy',
        question: 'DAML source files use which extension?',
        choices: { a: '.dml', b: '.daml', c: '.dl', d: '.dar' },
        answer: 'b',
        explanation: 'Source is `.daml`; the compiled archive is a `.dar`.',
    },
    {
        id: 18,
        difficulty: 'easy',
        question: 'Which command-line tool builds and tests DAML projects?',
        choices: { a: 'the `cargo` CLI', b: 'the `daml` assistant', c: 'the `canton` console', d: 'the `forge` CLI' },
        answer: 'b',
        explanation: 'The `daml` CLI wraps the compiler, sandbox, scripts and more. The Canton console administers running nodes.',
    },
    {
        id: 19,
        difficulty: 'easy',
        question: 'Automated tests for DAML models are written using:',
        choices: { a: 'DAML Script', b: 'JUnit', c: 'Hardhat', d: 'pytest' },
        answer: 'a',
        explanation: 'DAML Script drives the ledger API from DAML code to set up and assert on scenarios.',
    },
    {
        id: 20,
        difficulty: 'easy',
        question: 'In `template Iou with issuer : Party; owner : Party; amount : Decimal where ...`, `issuer` and `owner` are:',
        choices: {
            a: 'template fields (parameters)',
            b: 'choices defined on the template',
            c: 'modules imported into the file',
            d: 'events emitted on creation',
        },
        answer: 'a',
        explanation: 'The `with` block lists the record fields that parameterize each contract instance.',
    },
    {
        id: 21,
        difficulty: 'easy',
        question: 'Which numeric type is idiomatic for money amounts in DAML?',
        choices: { a: 'Float', b: 'Decimal', c: 'BigInt', d: 'Money' },
        answer: 'b',
        explanation: '`Decimal` (a fixed-precision `Numeric 10`) avoids binary-floating-point rounding errors.',
    },
    {
        id: 22,
        difficulty: 'easy',
        question: 'The result type of ledger actions such as `create` and `exercise` is:',
        choices: { a: 'IO', b: 'Update', c: 'Promise', d: 'Task' },
        answer: 'b',
        explanation: 'Ledger effects are expressed in the `Update` action type; pure code uses plain values.',
    },
    {
        id: 23,
        difficulty: 'easy',
        question: '`getTime` inside a choice body returns:',
        choices: {
            a: 'the wall-clock time of the submitting machine',
            b: 'the ledger effective time of the transaction',
            c: 'the time the contract was first created',
            d: "the sequencer's timestamp for the block",
        },
        answer: 'b',
        explanation: "It returns the transaction's ledger time, which participants agree on and can validate (within a tolerance of the sequencer's record time).",
    },
    {
        id: 24,
        difficulty: 'easy',
        question: 'The stakeholders of a contract are:',
        choices: {
            a: 'its signatories and observers',
            b: 'its signatories only',
            c: 'every party on the ledger',
            d: 'its signatories and controllers',
        },
        answer: 'a',
        explanation: 'Stakeholders are the signatories plus the observers: the parties guaranteed to see the contract.',
    },
    {
        id: 25,
        difficulty: 'easy',
        question: 'Canton is best described as:',
        choices: {
            a: 'a privacy-focused ledger network that runs DAML',
            b: 'an optimistic rollup that settles DAML on Ethereum',
            c: 'a Bitcoin sidechain for tokenized assets',
            d: 'a zero-knowledge proof system for DAML contracts',
        },
        answer: 'a',
        explanation: 'Canton synchronizes DAML transactions across participants while keeping data need-to-know.',
    },
    {
        id: 26,
        difficulty: 'easy',
        question: "In Canton, the node a party connects to, which stores that party's contracts, is a:",
        choices: { a: 'miner', b: 'participant node', c: 'sequencer', d: 'mediator' },
        answer: 'b',
        explanation: 'Participant nodes host parties, hold their Active Contract Set, and submit/validate transactions.',
    },
    {
        id: 27,
        difficulty: 'easy',
        question: 'In Canton, participants synchronize transactions through a:',
        choices: { a: 'domain / synchronizer', b: 'global mempool', c: 'bridge smart contract', d: 'peer-to-peer gossip layer' },
        answer: 'a',
        explanation: 'A domain (synchronizer) provides ordering and delivery for the participants connected to it.',
    },
    {
        id: 28,
        difficulty: 'easy',
        question: "Can a DAML contract's fields be changed in place after it is created?",
        choices: {
            a: 'Yes, with an `update` statement on the ledger',
            b: 'No, archive it and create a new one',
            c: 'Only the signatories can edit its fields',
            d: 'Yes, within the transaction that created it',
        },
        answer: 'b',
        explanation: 'Contracts are immutable; state transitions are archive-plus-create in one atomic transaction.',
    },
    {
        id: 29,
        difficulty: 'easy',
        question: "Which best describes DAML's evaluation model?",
        choices: {
            a: 'Imperative, with mutable global variables and loops',
            b: 'Pure and functional; effects go through `Update`',
            c: 'Object-oriented, with class inheritance',
            d: 'Stack-based bytecode, like the EVM',
        },
        answer: 'b',
        explanation: 'Expressions are pure; anything touching the ledger is an explicit `Update` (or `Script`) value.',
    },
    {
        id: 30,
        difficulty: 'easy',
        question: 'What does `daml build` produce?',
        choices: {
            a: 'A `.dar` archive of compiled packages',
            b: 'EVM bytecode plus an ABI file',
            c: 'A Docker image of the participant',
            d: 'A SQL schema for the ledger tables',
        },
        answer: 'a',
        explanation: 'A DAR bundles the compiled DALF packages and can be uploaded to a ledger.',
    },

    // --- AVERAGE (40) ---
    {
        id: 31,
        difficulty: 'medium',
        question: 'For a `create` to succeed, the transaction must be authorized by:',
        choices: {
            a: "all of the new contract's signatories",
            b: 'at least one of its observers',
            c: 'the domain operator of the synchronizer',
            d: 'the submitting participant node alone',
        },
        answer: 'a',
        explanation: "Create authorization requires the full set of the new contract's signatories.",
    },
    {
        id: 32,
        difficulty: 'medium',
        question: 'Exercising a choice requires authorization from:',
        choices: {
            a: "the choice's controller(s)",
            b: 'every signatory and observer of the contract',
            c: "the contract's observers",
            d: 'the package maintainer',
        },
        answer: 'a',
        explanation: 'The acting parties must include the choice controllers; signatories implicitly authorize actions on their own contracts.',
    },
    {
        id: 33,
        difficulty: 'medium',
        question: '`fetch cid` succeeds only if the fetching party is:',
        choices: {
            a: 'a stakeholder of that contract',
            b: "the sequencer of the contract's domain",
            c: 'any party that knows the ContractId of it',
            d: "the author of the contract's package",
        },
        answer: 'a',
        explanation: 'You can only `fetch` contracts you are entitled to see (as signatory or observer, or via explicit disclosure); knowing the ID is not enough.',
    },
    {
        id: 34,
        difficulty: 'medium',
        question: 'A `key` declaration in a template must be accompanied by:',
        choices: { a: 'a `maintainer` expression', b: 'an `ensure` clause', c: 'a `nonconsuming` choice', d: 'an `observer` clause' },
        answer: 'a',
        explanation: "The maintainers are the parties responsible for guaranteeing the key's uniqueness.",
    },
    {
        id: 35,
        difficulty: 'medium',
        question: 'The maintainers of a contract key must be:',
        choices: {
            a: 'a subset of the signatories',
            b: 'a subset of the observers',
            c: 'disjoint from the signatories',
            d: 'exactly the choice controllers',
        },
        answer: 'a',
        explanation: 'Maintainers must be signatories so they have authority to assert key uniqueness.',
    },
    {
        id: 36,
        difficulty: 'medium',
        question: 'In Daml 2.x, two active contracts with the same template key and maintainers:',
        choices: {
            a: 'cannot both be active; keys are unique among active contracts',
            b: 'are merged automatically into a single contract by the maintainers',
            c: 'can coexist; keys are only advisory lookup hints',
            d: 'force the domain to split into two separate histories',
        },
        answer: 'a',
        explanation: 'Contract keys give at-most-one-active-contract semantics for a given (key, maintainers).',
    },
    {
        id: 37,
        difficulty: 'medium',
        question: '`lookupByKey` returns:',
        choices: {
            a: '`Optional (ContractId t)`: `None` if no key matches',
            b: 'the contract payload, or an error if none exists',
            c: 'a list of all contracts that ever had that key',
            d: 'a `Bool` saying whether an active contract with it exists',
        },
        answer: 'a',
        explanation: 'It resolves a key to an optional ContractId; `fetchByKey` instead fails when absent.',
    },
    {
        id: 38,
        difficulty: 'medium',
        question: 'A `nonconsuming choice` is a good fit for:',
        choices: {
            a: 'a read-only query or a repeated action on the contract',
            b: 'the single action that settles and closes a contract',
            c: 'changing who the signatories of the contract are',
            d: 'uploading a new package version to the ledger',
        },
        answer: 'a',
        explanation: 'Because it leaves the contract active, it can be exercised many times.',
    },
    {
        id: 39,
        difficulty: 'medium',
        question: '`preconsuming` and `postconsuming` choices differ from a plain consuming choice in:',
        choices: {
            a: 'when the contract is archived: before or after the body runs',
            b: 'which parties are required to authorize the exercise',
            c: 'whether the choice body may create new contracts',
            d: "whether the archival is visible to the contract's observers",
        },
        answer: 'a',
        explanation: '`preconsuming` archives before the body runs; `postconsuming` archives after; a plain choice archives as part of the exercise.',
    },
    {
        id: 40,
        difficulty: 'medium',
        question: 'Divulgence in DAML refers to:',
        choices: {
            a: 'a non-stakeholder seeing a contract as a side effect of a tx',
            b: "a governance vote that publishes a contract's data to all parties",
            c: 'the sequencer broadcasting contracts to every participant',
            d: 'a signatory delegating its authority to another party temporarily',
        },
        answer: 'a',
        explanation: 'A party learns the contract because it saw a transaction that used it. Divulged data is visible but carries no Active Contract Set guarantees.',
    },
    {
        id: 41,
        difficulty: 'medium',
        question: 'Sub-transaction privacy in Canton means:',
        choices: {
            a: 'each participant sees only the parts involving its parties',
            b: 'transactions are encrypted end to end and never validated',
            c: "only the sequencer can read a transaction's full contents",
            d: 'every participant sees the whole transaction but not amounts',
        },
        answer: 'a',
        explanation: 'A participant receives a blinded projection containing just the nodes with its informees.',
    },
    {
        id: 42,
        difficulty: 'medium',
        question: 'Does Canton have a single global total ordering of every transaction across all domains?',
        choices: {
            a: 'No, ordering is per domain; there is no global chain',
            b: 'Yes, one global chain ordered like Bitcoin blocks',
            c: 'Yes, the mediators agree on a single global order',
            d: 'Only for the transactions that touch more than one domain',
        },
        answer: 'a',
        explanation: 'Each domain orders its own traffic; cross-domain workflows use contract reassignment.',
    },
    {
        id: 43,
        difficulty: 'medium',
        question: 'Within a Canton domain, which component orders and delivers messages?',
        choices: { a: 'the sequencer', b: 'the mediator', c: 'the participant', d: 'the compiler' },
        answer: 'a',
        explanation: 'The sequencer provides a consistent, ordered, multicast message stream to participants.',
    },
    {
        id: 44,
        difficulty: 'medium',
        question: 'Within a Canton domain, which component collects confirmations and enforces transaction atomicity without seeing payloads?',
        choices: { a: 'the mediator', b: 'the sequencer', c: 'the ledger API server', d: 'the navigator' },
        answer: 'a',
        explanation: 'The mediator aggregates participant confirmations against required thresholds and rules on accept/reject.',
    },
    {
        id: 45,
        difficulty: 'medium',
        question: 'A single DAML transaction touching contracts on two different Canton domains:',
        choices: {
            a: "isn't possible directly; one contract must be reassigned first",
            b: 'runs on both domains at once and is committed atomically by both',
            c: "is ordered by whichever domain's sequencer is faster",
            d: 'is split into two independent transactions automatically',
        },
        answer: 'a',
        explanation: 'Execution happens on one domain; cross-domain flows use unassign/assign (reassignment) first.',
    },
    {
        id: 46,
        difficulty: 'medium',
        question: 'The DAML `Update` action type is used:',
        choices: {
            a: 'in choice bodies and other code with ledger effects',
            b: 'only inside DAML Script tests, never in templates',
            c: 'to declare the fields and parties of a template',
            d: 'to configure the domain a contract lives on',
        },
        answer: 'a',
        explanation: '`Update a` sequences ledger effects (create/exercise/fetch) and yields a value of type `a`.',
    },
    {
        id: 47,
        difficulty: 'medium',
        question: 'An `assertMsg`/`assert` that fails inside a choice body:',
        choices: {
            a: 'aborts the whole transaction, rolling back every effect',
            b: 'logs a warning and lets the choice body continue',
            c: 'archives only the current contract, then stops the body',
            d: 'retries the choice with the same arguments once',
        },
        answer: 'a',
        explanation: 'Any failure in the `Update` aborts the transaction atomically.',
    },
    {
        id: 48,
        difficulty: 'medium',
        question: 'Which statement about `Party` values is TRUE?',
        choices: {
            a: "They are opaque; code can't forge authority for a party",
            b: "They are 20-byte hashes of a party's public key",
            c: 'Any choice body can mint a new Party value and then act as it',
            d: "They always equal the submitting participant's ID",
        },
        answer: 'a',
        explanation: 'Authorization is granted by the parties actually submitting/authorizing, not synthesizable in code.',
    },
    {
        id: 49,
        difficulty: 'medium',
        question: '`Optional a` in DAML corresponds to:',
        choices: {
            a: "Haskell's `Maybe a`: `Some x` or `None`",
            b: 'a nullable pointer that may be `null`',
            c: 'a future value resolved after commit',
            d: 'a list that holds zero or more values',
        },
        answer: 'a',
        explanation: 'DAML has no `null`; absence is modelled with `Optional`.',
    },
    {
        id: 50,
        difficulty: 'medium',
        question: '`archive cid` is shorthand for:',
        choices: {
            a: 'exercising the built-in `Archive` choice',
            b: "deleting the contract from the participant's disk",
            c: 'moving the contract to a cold-storage domain',
            d: 'compressing the contract payload in history',
        },
        answer: 'a',
        explanation: 'Every template has an implicit `Archive` choice controlled by its signatories.',
    },
    {
        id: 51,
        difficulty: 'medium',
        question: 'To transfer an Iou to a new owner, a typical choice will:',
        choices: {
            a: 'archive the Iou and create a new one with the new owner',
            b: 'update the `owner` field of the existing Iou in place',
            c: "emit a Transfer event that the owner's node applies",
            d: 'create a second Iou and leave the first one active',
        },
        answer: 'a',
        explanation: 'State changes are always archive + create within one atomic transaction.',
    },
    {
        id: 52,
        difficulty: 'medium',
        question: "Changing a contract's observers requires:",
        choices: {
            a: 'archiving it and creating a new version with new observers',
            b: 'an admin command sent to the hosting participant node',
            c: 'editing the `.daml` source and redeploying the package',
            d: 'a topology transaction on the domain the contract lives on',
        },
        answer: 'a',
        explanation: 'Observers are fixed for a given contract instance because contracts are immutable.',
    },
    {
        id: 53,
        difficulty: 'medium',
        question: 'In Canton, who signs on behalf of a normal (locally hosted) party?',
        choices: {
            a: 'the participant node hosting that party',
            b: "the sequencer of the party's domain",
            c: "the mediator of the party's domain",
            d: 'a signing service run by the domain owner',
        },
        answer: 'a',
        explanation: 'Local parties act through their hosting participant, which controls the corresponding keys.',
    },
    {
        id: 54,
        difficulty: 'medium',
        question: 'A DAML package on a ledger is identified by:',
        choices: {
            a: 'its package ID (a hash of the compiled package)',
            b: "its file path in the project's `daml/` folder",
            c: 'the ID of the domain it was first uploaded to',
            d: 'a sequential number assigned on upload',
        },
        answer: 'a',
        explanation: 'Templates are referenced by (package ID, module, name).',
    },
    {
        id: 55,
        difficulty: 'medium',
        question: 'The Navigator tool is:',
        choices: {
            a: 'a web UI to browse contracts and exercise choices locally',
            b: 'the production consensus engine for Canton domains',
            c: 'a static analyzer that flags authorization bugs in DAML models',
            d: 'a registry for publishing reusable DAML packages',
        },
        answer: 'a',
        explanation: 'Navigator connects to the ledger API and lets you inspect the Active Contract Set, mainly for local development.',
    },
    {
        id: 56,
        difficulty: 'medium',
        question: 'DAML "triggers" are:',
        choices: {
            a: 'off-ledger automation that reacts to events and submits commands',
            b: 'on-ledger hooks that run automatically when a contract is created',
            c: 'compiler pragmas that inline choices at build time',
            d: 'choices that fire automatically at a set ledger time',
        },
        answer: 'a',
        explanation: 'A trigger is a DAML rule engine running as a client, reacting to ACS changes.',
    },
    {
        id: 57,
        difficulty: 'medium',
        question: '`createAndExercise` is useful for:',
        choices: {
            a: 'creating a contract and exercising a choice on it in one step',
            b: 'skipping the controller authorization checks for the choice entirely',
            c: 'creating many contracts in a single loop',
            d: 'creating a contract that has no signatories at all',
        },
        answer: 'a',
        explanation: 'It fuses a create and an exercise into one command in a single transaction, without needing the ContractId first.',
    },
    {
        id: 58,
        difficulty: 'medium',
        question: 'DAML `interface` declarations let you:',
        choices: {
            a: 'program against shared choices and a view across many templates',
            b: 'call external REST APIs from inside a choice body safely',
            c: 'define the network settings of a Canton domain',
            d: 'inherit the fields and choices of a parent template, like classes',
        },
        answer: 'a',
        explanation: 'Interfaces give polymorphism over templates; DAML has no template inheritance.',
    },
    {
        id: 59,
        difficulty: 'medium',
        question: 'If a choice body tries to `create` a contract whose signatories have not authorized the transaction:',
        choices: {
            a: 'the transaction fails with an authorization error',
            b: 'the contract is created but flagged as unauthorized',
            c: 'the missing signatories are asked to confirm later',
            d: 'it succeeds if the controller signs any other contract',
        },
        answer: 'a',
        explanation: "Every create must be fully authorized by the new contract's signatories.",
    },
    {
        id: 60,
        difficulty: 'medium',
        question: "Canton's per-domain consistency guarantee is that transactions are:",
        choices: {
            a: 'atomic: if rejected, no participant applies any of it',
            b: 'eventually consistent, with possible partial application',
            c: 'best-effort, with manual reconciliation after failures',
            d: 'atomic within each participant, but not across participants',
        },
        answer: 'a',
        explanation: 'Either all involved participants commit the transaction or none do.',
    },
    {
        id: 61,
        difficulty: 'medium',
        question: 'The signatories/observers/`ensure`/`key` expressions in a template must be:',
        choices: {
            a: 'pure, total functions of the template fields',
            b: 'allowed to fetch other contracts to decide visibility',
            c: 'evaluated only by the mediator, never participants',
            d: 'written as `Update` actions returning parties',
        },
        answer: 'a',
        explanation: 'They are evaluated to decide validity and visibility, so they must be deterministic and total (no `Update`, no failure).',
    },
    {
        id: 62,
        difficulty: 'medium',
        question: '`exerciseByKey @T key ChoiceName with ...` requires the submitter to be:',
        choices: {
            a: "a key maintainer, plus the choice's controllers",
            b: 'the operator of the domain the contract lives on',
            c: 'any observer of the contract, without further checks',
            d: "the party that uploaded the template's package",
        },
        answer: 'a',
        explanation: "Key-based lookup needs maintainer authority in addition to the choice's own controllers.",
    },
    {
        id: 63,
        difficulty: 'medium',
        question: 'Deterministic execution is required in DAML because:',
        choices: {
            a: 'every involved participant re-validates and must agree',
            b: 'the DAML compiler can only emit single-threaded code',
            c: 'floating-point numbers are banned from the language',
            d: 'the sequencer replays every contract nightly to audit it',
        },
        answer: 'a',
        explanation: 'Non-determinism would make participants disagree, breaking atomic commit.',
    },
    {
        id: 64,
        difficulty: 'medium',
        question: 'The `with` record in `template X with f1 : T1; f2 : T2 where` is:',
        choices: {
            a: 'the fields that parameterize each contract instance',
            b: 'the list of choices available on the template',
            c: 'the modules and packages the template imports',
            d: 'the parties who are allowed to see each contract',
        },
        answer: 'a',
        explanation: 'Contract instances are records of these fields; two contracts are distinct if any field differs.',
    },
    {
        id: 65,
        difficulty: 'medium',
        question: 'A DAML Script that needs two parties typically obtains them via:',
        choices: {
            a: '`allocateParty` (or `allocatePartyWithHint`)',
            b: "hard-coded hex strings for each party's key",
            c: '`createParty`, which mints them on the ledger',
            d: "reading them from the sequencer's event log",
        },
        answer: 'a',
        explanation: 'Scripts allocate parties on the test ledger, then submit commands as them.',
    },
    {
        id: 66,
        difficulty: 'medium',
        question: 'The set of contracts a participant currently knows to be active is called its:',
        choices: { a: 'Active Contract Set (ACS)', b: 'Pending Transaction Pool', c: 'Unspent Contract Set', d: 'Contract State Trie' },
        answer: 'a',
        explanation: 'The ACS is the participant-local view of currently-active contracts for its parties.',
    },
    {
        id: 67,
        difficulty: 'medium',
        question: "A choice's return type can be:",
        choices: {
            a: 'any serializable type, e.g. a new ContractId',
            b: 'only `()`, since choices are pure actions',
            c: 'only `Bool`, reporting success or failure',
            d: 'any type, including functions and `Update`s',
        },
        answer: 'a',
        explanation: 'Choices commonly return `ContractId T` for a contract they create; the value must be serializable, so no functions.',
    },
    {
        id: 68,
        difficulty: 'medium',
        question: 'Which DAML language feature enables adding an interface to a template you do not want to edit?',
        choices: {
            a: "an `interface instance` declared in the interface's module",
            b: "monkey-patching the template's choices at runtime",
            c: 'a domain flag that attaches interfaces to templates',
            d: 'a subclass of the original template that adds the interface to it',
        },
        answer: 'a',
        explanation: 'Interface instances can be declared in either the template or the interface module (retroactive instances).',
    },
    {
        id: 69,
        difficulty: 'medium',
        question: 'The `viewtype` of a DAML interface defines:',
        choices: {
            a: "the record type the interface's `view` returns",
            b: "the type of the interface's key, used for lookups",
            c: 'which parties are allowed to view the contract',
            d: 'the binary encoding used to store the contract',
        },
        answer: 'a',
        explanation: '`view` gives interface callers a typed snapshot without knowing the concrete template.',
    },
    {
        id: 70,
        difficulty: 'medium',
        question: 'A `flexible controller` (controller written in the `choice ... with` form rather than `controller c can`) differs in that:',
        choices: {
            a: "the controller isn't implicitly made an observer",
            b: 'the controller can never also be one of the signatories',
            c: 'the controller bypasses authorization checks',
            d: 'the controller must be one hard-coded party',
        },
        answer: 'a',
        explanation: 'The older `controller ... can` syntax implicitly added the controller as an observer; the modern form does not.',
    },

    // --- VERY HARD (30) ---
    {
        id: 71,
        difficulty: 'hard',
        question: 'The precise create-authorization rule for a node inside a transaction is:',
        choices: {
            a: 'its signatories must all be parent signatories or choice controllers',
            b: 'any single signatory of any contract used in the transaction',
            c: "the submitting participant's operator key, which covers every create",
            d: 'no authorization at all; only exercises need authorizing',
        },
        answer: 'a',
        explanation: 'Authority flows down from the parent exercise; a create is well-authorized only if its signatories are within that authority.',
    },
    {
        id: 72,
        difficulty: 'hard',
        question: 'Why can a consuming choice always archive its contract?',
        choices: {
            a: "signatories' authority comes with any exercise on the contract",
            b: 'archival needs no authorization from any party at all',
            c: "the mediator co-signs every archive on the stakeholders' behalf",
            d: 'the choice controller is always required to be a signatory',
        },
        answer: 'a',
        explanation: "Exercising a contract carries its signatories' authority, which is exactly what archiving it requires.",
    },
    {
        id: 73,
        difficulty: 'hard',
        question: 'In Canton 2.x, contract-key uniqueness is enforced:',
        choices: {
            a: 'by the participants hosting the maintainers, not a global index',
            b: 'by a global key-registry contract that every domain shares',
            c: 'by the sequencer, which scans every domain for duplicates',
            d: 'by the mediator, which keeps a table of every active key it sees',
        },
        answer: 'a',
        explanation: 'It is a participant-local guarantee: only participants hosting the maintainers can assert no active contract has a given key.',
    },
    {
        id: 74,
        difficulty: 'hard',
        question: 'Why is a negative `lookupByKey` (returning `None`) a sensitive operation?',
        choices: {
            a: 'a "no such contract" claim needs maintainer authority to verify',
            b: 'it silently creates a placeholder contract for that key if none exists',
            c: "it reveals the maintainers' signing keys to the submitter",
            d: 'it bypasses authorization, so anyone can probe for keys',
        },
        answer: 'a',
        explanation: 'A negative lookup is a statement about global state that every re-validating participant must be able to check, so it needs maintainer authorization/visibility.',
    },
    {
        id: 75,
        difficulty: 'hard',
        question: 'Explicit disclosure (disclosed contracts) was added to Canton to:',
        choices: {
            a: "use a contract you don't observe by attaching it to the command",
            b: 'publish a contract to a public explorer for anyone to read',
            c: "let a party act on a contract without its signatories' authorization",
            d: 'switch off sub-transaction privacy for chosen templates',
        },
        answer: 'a',
        explanation: 'The disclosed payload (plus proof) replaces reliance on divulgence and is checked against its contract ID and ledger state.',
    },
    {
        id: 76,
        difficulty: 'hard',
        question: 'A key difference between a divulged contract and an observed contract is:',
        choices: {
            a: 'divulged contracts get no ACS updates, e.g. about archival',
            b: 'divulged contracts are stored on the domain, not the participant',
            c: 'divulged contracts can never be fetched in a later transaction',
            d: 'observed contracts can be exercised by the observer at will',
        },
        answer: 'a',
        explanation: 'Only stakeholders get ongoing activeness guarantees; a divulged contract may be archived without the divulgee ever learning. Observers cannot exercise unless they are controllers.',
    },
    {
        id: 77,
        difficulty: 'hard',
        question: 'Canton achieves atomic commit across multiple participants via:',
        choices: {
            a: 'two-phase commit: confirmations aggregated by the mediator',
            b: 'proof-of-work mining done by the participant nodes',
            c: 'a trusted operator that holds all the transaction data',
            d: 'optimistic execution with fraud proofs on each domain',
        },
        answer: 'a',
        explanation: 'Participants that see each node send confirmations, ordered by the sequencer; the mediator checks thresholds, giving all-or-nothing without any node seeing everything.',
    },
    {
        id: 78,
        difficulty: 'hard',
        question: 'What does the Canton mediator learn about a transaction?',
        choices: {
            a: 'the blinded tree and who must confirm, but not payloads',
            b: 'the full plaintext of every contract in the transaction',
            c: 'nothing at all; it only relays sequencer messages',
            d: 'the payloads, but not which parties are involved',
        },
        answer: 'a',
        explanation: 'The mediator sees the informee tree needed to check confirmations, but payloads stay encrypted to participants.',
    },
    {
        id: 79,
        difficulty: 'hard',
        question: "In Canton's trust model, participants must trust a domain for:",
        choices: {
            a: 'liveness and ordering, not data integrity or privacy',
            b: 'everything, including validating all business logic',
            c: "custody of every hosted party's signing keys and funds",
            d: 'privacy only; integrity is checked by the sequencer',
        },
        answer: 'a',
        explanation: 'A malicious domain can censor or reorder, but cannot forge valid transactions or read encrypted payloads.',
    },
    {
        id: 80,
        difficulty: 'hard',
        question: 'Hosting a party on multiple participants (for availability/BFT) uses:',
        choices: {
            a: 'party-to-participant mappings with a confirmation threshold',
            b: 'a single private key file copied between the participants',
            c: 'a DAML contract that votes on each command for the party',
            d: 'round-robin load balancing across identical participant nodes',
        },
        answer: 'a',
        explanation: 'Topology transactions map a party to N participants with a threshold, giving fault tolerance.',
    },
    {
        id: 81,
        difficulty: 'hard',
        question: "Canton bounds the gap between a transaction's ledger time and record time using:",
        choices: {
            a: 'domain-configured skew bounds on ledger vs record time',
            b: 'an NTP client on each participant, trusted as-is',
            c: "the wall clock of the user's device that submits it",
            d: 'no bound at all; the submitter picks any ledger time',
        },
        answer: 'a',
        explanation: 'A transaction outside the tolerance window is rejected, which keeps `getTime` meaningful and prevents replay far outside the intended window.',
    },
    {
        id: 82,
        difficulty: 'hard',
        question: 'DAML "Smart Contract Upgrade" lets a ledger transparently use template v2 for v1 contracts as long as v2:',
        choices: {
            a: 'is a compatible superset: new fields optional, same key type',
            b: 'adds new required fields that old contracts will fill in',
            c: 'keeps the template name but is free to change the key type',
            d: 'lives in a module with the same name, whatever else changes',
        },
        answer: 'a',
        explanation: 'Upgrade compatibility requires strict structural rules (optional additions, unchanged key, compatible signatories/observers) so old contracts still typecheck.',
    },
    {
        id: 83,
        difficulty: 'hard',
        question: "Under Smart Contract Upgrade, changing a template's `key` type between package versions is:",
        choices: {
            a: 'not allowed; the key type must stay identical',
            b: 'allowed, and it speeds up key lookups over time',
            c: 'fine, as long as the maintainers stay the same',
            d: 'allowed only for templates with no active contracts',
        },
        answer: 'a',
        explanation: 'Keys anchor contract identity for lookups, so they cannot change during an upgrade.',
    },
    {
        id: 84,
        difficulty: 'hard',
        question: 'Canton "reassignment" (unassign/assign, formerly transfer-out/transfer-in) moves a contract:',
        choices: {
            a: 'from one domain to another: unassigned, then assigned',
            b: 'between participants, by copying the contract files',
            c: 'between two owners who are hosted on the same domain',
            d: 'from a test network onto the production network',
        },
        answer: 'a',
        explanation: 'It becomes inactive on the source, then active on the target. This is how a contract changes which synchronizer governs it.',
    },
    {
        id: 85,
        difficulty: 'hard',
        question: 'An "informee" of a transaction node is:',
        choices: {
            a: 'a party that must see the node, e.g. its stakeholders',
            b: 'the sequencer that delivers the node to participants',
            c: 'a party explicitly excluded from ever seeing the node',
            d: 'the participant that submitted the whole transaction',
        },
        answer: 'a',
        explanation: 'Informees (stakeholders of affected contracts, acting parties) determine projections: each participant keeps exactly the nodes where it hosts an informee.',
    },
    {
        id: 86,
        difficulty: 'hard',
        question: 'How does Canton let a participant that only sees part of a transaction still be sure the whole transaction is valid?',
        choices: {
            a: 'parts are confirmed by those who see them; the mediator aggregates',
            b: "it trusts the submitting participant's signed claim of validity",
            c: 'it re-downloads and re-executes the full transaction from peers',
            d: 'a zk-SNARK attached to the transaction proves all of the hidden parts',
        },
        answer: 'a',
        explanation: 'Validity is established piecewise by the parties with visibility; the mediator checks the confirmations meet the required thresholds for the whole tree.',
    },
    {
        id: 87,
        difficulty: 'hard',
        question: "Why can't Canton simply put all data on one shared blockchain that every node replicates?",
        choices: {
            a: "every node would see data for parties it doesn't host",
            b: "blockchains can't store DAML's typed contract data",
            c: "full replication would be too slow for DAML's execution model",
            d: 'it would need a native token to pay for block space',
        },
        answer: 'a',
        explanation: "Canton's design keeps data strictly need-to-know, which a fully-replicated chain cannot do.",
    },
    {
        id: 88,
        difficulty: 'hard',
        question: '`fetchByKey` versus `lookupByKey`:',
        choices: {
            a: '`fetchByKey` aborts when absent; `lookupByKey` returns `None`',
            b: '`lookupByKey` aborts when absent; `fetchByKey` returns `None`',
            c: 'both abort when absent; `lookupByKey` also needs no authority',
            d: 'they are exact synonyms kept for backward compatibility',
        },
        answer: 'a',
        explanation: 'Choose `fetchByKey` when absence is a bug; `lookupByKey` when absence is a valid branch. Both require maintainer authorization.',
    },
    {
        id: 89,
        difficulty: 'hard',
        question: 'An `interface` that `requires` another interface means an implementing template must:',
        choices: {
            a: 'also implement every required interface',
            b: "inherit the other interface's storage layout",
            c: 'live in the same package as the interface',
            d: 'have no choices of its own besides Archive',
        },
        answer: 'a',
        explanation: '`requires` builds a directed hierarchy so code can rely on the combined choice set and views.',
    },
    {
        id: 90,
        difficulty: 'hard',
        question: 'Authority in a DAML subtransaction does NOT include:',
        choices: {
            a: 'authority from unrelated contracts in the same transaction',
            b: 'the signatories of the contract being exercised',
            c: 'the controllers of the choice being exercised',
            d: 'authority those parties delegated via a contract they signed',
        },
        answer: 'a',
        explanation: 'Authority does not "leak" sideways; each action is bounded by its own parent\'s authorizers (contract signatories plus choice controllers).',
    },
    {
        id: 91,
        difficulty: 'hard',
        question: 'Daml Finance is:',
        choices: {
            a: 'a library of standard templates for financial apps',
            b: 'a stablecoin that Digital Asset issues on the Canton Network',
            c: 'the fee token used to pay for Canton Network traffic',
            d: 'a regulated custodian that holds assets for Canton apps',
        },
        answer: 'a',
        explanation: "It provides reusable templates and interfaces (holdings, instruments, settlement, lifecycling). It is not part of the language core. The network's fee token is Canton Coin.",
    },
    {
        id: 92,
        difficulty: 'hard',
        question: "The Canton Network's Global Synchronizer is designed so that transaction ordering is:",
        choices: {
            a: 'done by a decentralized, BFT set of sequencer nodes',
            b: 'done by a single company that operates it indefinitely',
            c: "inherited from Ethereum's block ordering via a bridge",
            d: 'done by each participant independently, in random order',
        },
        answer: 'a',
        explanation: 'A BFT sequencer group removes single-operator control over ordering and liveness.',
    },
    {
        id: 93,
        difficulty: 'hard',
        question: 'Why must `signatory`, `observer`, `ensure` and `key` never perform `Update` actions or throw?',
        choices: {
            a: 'they decide validity/visibility and must be total and deterministic',
            b: "the compiler's optimizer can't handle effects or exceptions in them",
            c: 'they run on the mediator, which has no access to ledger state',
            d: 'it is only a style guideline, enforced by the linter as a warning',
        },
        answer: 'a',
        explanation: 'These functions define contract semantics themselves, evaluated by every participant during validation, so they must always produce a definite answer.',
    },
    {
        id: 94,
        difficulty: 'hard',
        question: 'Explicit disclosure is safe against forgery because the attached contract payload is:',
        choices: {
            a: 'checked against its contract ID and confirmed to be active',
            b: 'signed by the domain owner at the moment it is disclosed',
            c: 'trusted because the submitting party vouches for it',
            d: 'encrypted so the submitter cannot inspect or alter it',
        },
        answer: 'a',
        explanation: 'The contract ID commits to the create arguments; you cannot fabricate a payload whose hash matches a real, active contract ID.',
    },
    {
        id: 95,
        difficulty: 'hard',
        question: 'A DAML `Update` that both `create`s a child and returns its `ContractId`, then the parent choice fails an `assert` afterwards:',
        choices: {
            a: 'rolls back everything, including the child create',
            b: 'keeps the child contract but marks the parent failed',
            c: 'keeps the child if another party already observed it',
            d: 'commits the child later, once the parent is retried',
        },
        answer: 'a',
        explanation: 'There is no partial commit: an aborted transaction leaves the ledger unchanged.',
    },
    {
        id: 96,
        difficulty: 'hard',
        question: 'Canton "party migration" between participants preserves the party\'s contracts by:',
        choices: {
            a: "moving the party's ACS snapshot and updating topology mappings",
            b: 're-running every historical transaction from genesis on the new node',
            c: 'minting fresh copies of the contracts with new ContractIds',
            d: 'asking every counterparty to re-sign each contract',
        },
        answer: 'a',
        explanation: 'Export/import of the Active Contract Set plus a party-to-participant topology change moves hosting without changing contract identities.',
    },
    {
        id: 97,
        difficulty: 'hard',
        question: 'Why is DAML\'s ledger model described as giving each stakeholder a "consistent partial view"?',
        choices: {
            a: 'each sees a projection that agrees with others where they overlap',
            b: 'views are only eventually consistent and may briefly disagree',
            c: "only the signatories' view is authoritative; observers may lag",
            d: 'every stakeholder sees the whole ledger but with amounts hidden',
        },
        answer: 'a',
        explanation: 'Projections of the same global transaction tree agree on any node two parties both see, without anyone seeing the whole thing.',
    },
    {
        id: 98,
        difficulty: 'hard',
        question: 'A choice with a flexible `controller` expression that reads choice arguments still requires that:',
        choices: {
            a: 'those controller parties actually authorize the exercise',
            b: 'the controller is a field of the template, not an argument',
            c: "the controller is also the submitting participant's operator",
            d: 'nothing more, since a computed controller needs no signature',
        },
        answer: 'a',
        explanation: "Computing who the controller is does not grant that party's authority; they must be acting parties in the submission.",
    },
    {
        id: 99,
        difficulty: 'hard',
        question: "In Canton, a domain that tries to censor a participant's transactions:",
        choices: {
            a: 'can hurt its liveness, but not forge or read its transactions',
            b: 'can silently rewrite the balances held in its contracts',
            c: 'can impersonate any party that participant hosts, at will',
            d: 'gains nothing, because censorship is impossible there',
        },
        answer: 'a',
        explanation: "Censorship (a liveness attack) is the domain's worst-case power; integrity and privacy are protected cryptographically.",
    },
    {
        id: 100,
        difficulty: 'hard',
        question: 'Why does DAML forbid mutating a contract in place, forcing archive + create for every change?',
        choices: {
            a: 'each change becomes an explicit, authorized, auditable event',
            b: 'overwriting storage in place costs more than creating anew',
            c: 'the DAML compiler has no way to express field assignment at all',
            d: 'purely for historical reasons, with no practical benefit',
        },
        answer: 'a',
        explanation: 'Each transition is a first-class, authorized, atomically-validated ledger event that all stakeholders can agree on, rather than an opaque field write.',
    },
];
